import { ethers } from 'ethers'
import { TronWeb } from 'tronweb'
import { formatUnits, isAddress, TransactionReceipt, zeroAddress } from 'viem'

import { FromChain, ToChain } from '@/hooks/services/useChainInfo'
import { TokenMeta } from '@/hooks/services/useTokenInfo'

import { CommonTools } from './CommonTools'

export default class TronTools implements CommonTools {
  accountAddress?: string | undefined
  isConnected: boolean = false
  bridgeAddress: string = import.meta.env.VITE_TRON_BRIDGE_ADDRESS
  tron: any
  tronWeb: TronWeb

  constructor(chain: FromChain, onConnect: (address: string, isConnected: boolean) => void) {
    if (!window.tokenpocket.tron || !window.tokenpocket.tronWeb)
      throw new Error('No EVM wallet detected. Please install TokenPocket.')

    console.log('>>>>>> chain: ', chain)

    this.tron = window.tokenpocket.tron
    this.tronWeb = window.tokenpocket.tronWeb
    this.bridgeAddress = this.tronWeb.address.toHex(chain.bridgeAddress)

    this.connect(onConnect)
  }

  async connect(onConnect: (address: string, isConnected: boolean) => void) {
    const addresses = await this.tron.request({ method: 'tron_requestAccounts' })
    this.accountAddress = addresses[0] as string
    this.isConnected = true
    onConnect(this.accountAddress, this.isConnected)
  }

  async bridge(parmas: {
    fromChain: FromChain
    toChain: ToChain
    fromToken: TokenMeta
    toToken: TokenMeta
    receviceAddress: string
    paymentAddress: string
    amount: number
    challengePeriod: number
    fee: BigNumber
    nativeFee: BigNumber
  }): Promise<TransactionReceipt | undefined> {
    if (!this.accountAddress) return
    console.log('>>>>>> parmas: ', parmas)
    const {
      fromChain,
      toChain,
      fromToken,
      toToken,
      receviceAddress,
      amount,
      challengePeriod,
      nativeFee = new BigNumber(0),
      fee = new BigNumber(0)
    } = parmas

    const bridgeContract = await this.tronWeb.contract().at(this.bridgeAddress)
    const isNative = fromToken.address.toLowerCase() === zeroAddress
    const nativeFeeBN = ethers.utils.parseUnits(nativeFee.toString(), 6)
    if (isNative) {
      const feeBN = ethers.utils.parseUnits(fee.toString(), 6)
      const amount0 = ethers.utils.parseUnits(amount.toString(), 6)
      const amount1 = amount0.sub(feeBN)

      return await bridgeContract
        .receiveEther(
          BigInt(fromChain.id),
          BigInt(toChain.id),
          toToken.address,
          isAddress(receviceAddress) ? receviceAddress : this.accountAddress,
          amount0.toBigInt(),
          amount1.toBigInt(),
          feeBN.toBigInt(),
          nativeFeeBN.toBigInt(),
          BigInt(challengePeriod),
          ''
        )
        .send({
          callValue: amount0.add(nativeFeeBN).toBigInt()
        })
    } else {
      const tokenContract = await this.tronWeb.contract().at(fromToken.address)
      const allowance = await tokenContract.allowance(this.accountAddress, this.bridgeAddress).call()

      const feeBN = ethers.utils.parseUnits(fee.toString(), fromToken.decimal)
      const amount0 = ethers.utils.parseUnits(amount.toString(), fromToken.decimal)
      const amount1 = amount0.sub(feeBN)

      if (allowance.toBigInt() < amount0.toBigInt()) {
        await tokenContract.approve(this.bridgeAddress, amount0).send()
      }

      return await bridgeContract
        .receiveToken(
          BigInt(fromChain.id),
          fromToken.address,
          BigInt(toChain.id),
          toToken.address,
          isAddress(receviceAddress) ? receviceAddress : this.accountAddress,
          amount0.toBigInt(),
          amount1.toBigInt(),
          feeBN.toBigInt(),
          nativeFeeBN.toBigInt(),
          BigInt(challengePeriod),
          ''
        )
        .send({
          callValue: nativeFeeBN.toBigInt()
        })
    }
  }

  async getBalance(tokenMeta: TokenMeta): Promise<string | undefined> {
    if (!this.accountAddress) return
    if (tokenMeta.address === zeroAddress) {
      const balance = await this.tronWeb.trx.getBalance(this.accountAddress)
      return this.tronWeb.fromSun(balance).toString()
      // return formatUnits(balance.toBigInt(), 6)
    } else {
      const contract = await this.tronWeb.contract().at(tokenMeta.address)
      const balance = await contract.balanceOf(this.accountAddress).call()

      return formatUnits(balance.toBigInt(), tokenMeta.decimal)
    }
  }
}
